/**
 * Shared bits for the two role-aware sales cards (AnticipatedEarnings +
 * MilestoneBonuses):
 *  - money / compact formatters
 *  - usePageSize: measures the roster area and returns how many rows fit, so
 *    the card stays a FIXED height and paginates instead of growing.
 *  - Segmented (Team / As rep toggle), RepPicker, Pager sub-components.
 *
 * Both cards take a single `data` prop (the /group/:id/bonus-progress payload
 * plus `myId` + `connected`): { connected, isAdmin, myId, config, reps, totals }.
 */
import React, { useEffect, useLayoutEffect, useState } from "react";
import Select, { components as RSComponents } from "react-select";
import SoftBox from "../SoftBox";
import { GET_IMAGE } from "../../helpers/images";

export const FONT = "DM Sans, Inter, sans-serif";
export const C = {
  // Tokenized so both sales cards flip dark on the TV (.tv-board.tv-dark defines
  // --tvd-*) while staying light on the home page, where the tokens are undefined
  // and each falls back to its original light-mode hex.
  ink: "var(--tvd-ink, #0F172A)", // matches the dashboard card-title ink (KOTM, Team Performance, Leaderboard)
  muted: "var(--tvd-body, #667085)",
  faint: "var(--tvd-muted, #98A2B3)",
  line: "var(--tvd-hair, #EAECF0)",
  soft: "var(--tvd-tile, #F2F4F7)",
  green: "#12B76A",
};

export const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
export const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(v % 1e6 ? 1 : 0)}M`;
  // One decimal when it isn't a whole thousand — Math.round() collapsed
  // $2,500 and $3,000 both to "$3K", so two different rungs read identically.
  if (v >= 1e3) return `$${(v / 1e3).toFixed(v % 1e3 ? 1 : 0)}K`;
  return `$${Math.round(v)}`;
};
// Pay amounts never round away real money: $1,250 must read "$1.25K", not
// "$1.3K" (up to 2 decimals, trailing zeros dropped).
export const compactPay = (n) => {
  const v = Number(n) || 0;
  const trim = (s) => s.replace(/\.00$|0$/, "").replace(/\.$/, "");
  if (v >= 1e6) return `$${trim((v / 1e6).toFixed(2))}M`;
  if (v >= 1e3) return `$${trim((v / 1e3).toFixed(2))}K`;
  return `$${trim(v.toFixed(2))}`;
};

/** Rows that fit in the roster area at the card's current height. */
export function usePageSize(ref, rowHeight, deps = []) {
  const [size, setSize] = useState(4);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const h = el.clientHeight;
      if (h <= 0) return;
      // Prefer a real rendered row over the rowHeight estimate — the estimate
      // ran a few px short, which let one extra row "fit" and clipped the last
      // one. The second child carries the top border every non-first row has,
      // so it is the true repeating height. Sanity-bound the probe so the
      // full-height "no data" placeholder can't be mistaken for a row.
      const probe = el.children[1] || el.children[0];
      const probeH = probe ? probe.offsetHeight : 0;
      const rh = probeH >= 20 && probeH <= rowHeight * 2 ? probeH : rowHeight;
      setSize(Math.max(1, Math.floor(h / rh)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return size;
}

export function Segmented({ mode, setMode, primary }) {
  const btn = (val, label) => {
    const on = mode === val;
    return (
      <SoftBox
        component="button"
        type="button"
        onClick={() => setMode(val)}
        sx={{
          border: 0,
          background: on ? "var(--tvd-card, #FFFFFF)" : "transparent",
          boxShadow: on ? "0 1px 2px rgba(16,24,40,.10)" : "none",
          color: on ? primary : C.muted,
          fontFamily: FONT,
          fontSize: "11px",
          fontWeight: 600,
          // Taller tap target on mobile only — desktop keeps the original padding.
          padding: { mini: "9px 10px", sm: "4px 10px" },
          borderRadius: "6px",
          cursor: "pointer",
        }}
      >
        {label}
      </SoftBox>
    );
  };
  return (
    <SoftBox sx={{ display: "inline-flex", background: C.soft, borderRadius: "8px", padding: "2px", gap: "2px" }}>
      {btn("team", "Team")}
      {btn("rep", "As rep")}
    </SoftBox>
  );
}

// System light dropdown — replaces the native <select>s these surfaces used
// (native menus render OS chrome and misbehave inside the dashboard cards).
// Portalled to <body> so no card edge or overflow can clip the open menu.
const BRAND = "var(--gmb-primary, #1B598E)";
const optionBg = (state) => {
  if (state.isSelected) return "rgba(27,89,142,0.10)";
  if (state.isFocused) return "rgba(15,23,42,0.05)";
  return "transparent";
};
export const lightSelectStyles = (small = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: small ? "28px" : "42px",
    height: small ? "28px" : base.height,
    borderRadius: small ? "7px" : "9px",
    background: "var(--tvd-tile, #FFFFFF)",
    border: state.isFocused ? `1.5px solid ${BRAND}` : `1px solid ${C.line}`,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(27,89,142,0.08)" : "none",
    fontFamily: FONT,
    fontSize: small ? "12px" : "13.5px",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": { borderColor: state.isFocused ? BRAND : "#98A2B3" },
  }),
  valueContainer: (base) => ({ ...base, padding: small ? "0 4px 0 8px" : "2px 12px" }),
  singleValue: (base) => ({ ...base, color: C.ink }),
  placeholder: (base) => ({ ...base, color: C.faint, fontWeight: 500 }),
  dropdownIndicator: (base) => ({ ...base, padding: small ? "0 5px" : "0 8px", color: C.faint }),
  indicatorSeparator: () => ({ display: "none" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    minWidth: "170px",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(26px) saturate(185%)",
    WebkitBackdropFilter: "blur(26px) saturate(185%)",
    border: "1px solid rgba(255,255,255,0.6)",
    padding: "6px",
    boxShadow: "0 16px 40px -12px rgba(16,24,40,0.22), inset 0 1px 0 rgba(255,255,255,0.7)",
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    /* Roomy enough that a normal roster (a couple of roll-ups + a handful of
       players) fits with NO scrollbar at all; the thin bar below only shows for
       genuinely long lists, where its thumb is naturally short. */
    maxHeight: "320px",
    /* Thin, faint scrollbar — the default chunky/dark bar reads heavy in a
       small frosted menu. A ~4px near-transparent pill is all it needs. */
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(16,24,40,0.16) transparent",
    "::-webkit-scrollbar": { width: "3px", height: "3px" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "::-webkit-scrollbar-thumb": { background: "rgba(16,24,40,0.16)", borderRadius: "999px" },
    "::-webkit-scrollbar-thumb:hover": { background: "rgba(16,24,40,0.28)" },
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: FONT,
    fontSize: "13px",
    fontWeight: state.isSelected ? 600 : 500,
    borderRadius: "6px",
    padding: "8px 10px",
    cursor: "pointer",
    background: optionBg(state),
    color: state.isSelected ? BRAND : "#475467",
  }),
  /* Grouped picker: a small uppercase caption per section, and a hairline above
     every group but the first — so "Team" and "Players" read as separate sets
     rather than one packed list. */
  group: (base) => ({ ...base, padding: "2px 0" }),
  groupHeading: (base) => ({
    ...base,
    fontFamily: FONT,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: C.faint,
    padding: "8px 10px 4px",
    margin: 0,
  }),
});

/* A group with a hairline above it — except the first — so grouped pickers
   ("Team" / "Players") read as separate sets. Non-grouped pickers never render
   a Group, so this is inert for them. */
function DividedGroup(props) {
  const { Group } = RSComponents;
  const { selectProps, data } = props;
  const groups = selectProps.options || [];
  const isFirst = groups.length ? groups[0] === data : true;
  return (
    <div style={isFirst ? undefined : { borderTop: "1px solid #EEF2F7", marginTop: "4px", paddingTop: "2px" }}>
      <Group {...props} />
    </div>
  );
}

/* react-select flattens grouped options, so find a value across groups too. */
const findOption = (opts, value) => {
  for (let i = 0; i < (opts || []).length; i += 1) {
    const o = opts[i];
    if (o && Array.isArray(o.options)) {
      const hit = o.options.find((x) => x.value === value);
      if (hit) return hit;
    } else if (o && o.value === value) {
      return o;
    }
  }
  return null;
};

export function LightPicker({ options, value, onChange, placeholder, small = false, width }) {
  const opts = options || [];
  return (
    <SoftBox sx={{ minWidth: width || (small ? "150px" : "220px") }}>
      <Select
        options={opts}
        value={findOption(opts, value)}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        placeholder={placeholder}
        isSearchable={false}
        components={{ Group: DividedGroup }}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPlacement="auto"
        styles={lightSelectStyles(small)}
      />
    </SoftBox>
  );
}

export function RepPicker({ reps, value, onChange }) {
  return (
    <SoftBox sx={{ display: "flex", alignItems: "center", gap: "7px", fontFamily: FONT, fontSize: "12px", color: C.muted, mt: "6px" }}>
      Viewing
      <LightPicker
        small
        options={reps.map((r) => ({ value: r.ownerId, label: r.name }))}
        value={value}
        onChange={onChange}
      />
    </SoftBox>
  );
}

export function Pager({ page, pages, setPage }) {
  if (pages <= 1) return <span />;
  const arw = (dir, disabled) => (
    <SoftBox
      component="button"
      type="button"
      disabled={disabled}
      onClick={() => setPage(page + (dir === "next" ? 1 : -1))}
      aria-label={dir === "next" ? "Next page" : "Previous page"}
      sx={{
        // Bigger tap target on mobile only (26px was under the ~32-36px
        // comfortable-touch guideline) — desktop keeps the original 26px.
        width: { mini: "34px", sm: "26px" },
        height: { mini: "34px", sm: "26px" },
        borderRadius: "7px",
        border: `1px solid ${C.line}`,
        background: "var(--tvd-tile, #FFFFFF)",
        color: "var(--tvd-body, #475467)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.35 : 1,
        fontSize: "14px",
      }}
    >
      {dir === "next" ? "›" : "‹"}
    </SoftBox>
  );
  return (
    <SoftBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
      {arw("prev", page === 0)}
      <span style={{ fontFamily: FONT, fontSize: "11.5px", fontWeight: 500, color: C.muted, fontVariantNumeric: "tabular-nums" }}>
        {page + 1} of {pages}
      </span>
      {arw("next", page >= pages - 1)}
    </SoftBox>
  );
}

/** Small circular avatar with initials fallback. */
export function Avatar({ name, image, color = "#1B598E" }) {
  const initials = (name || "P")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return image ? (
    <SoftBox
      component="img"
      // image may be a full URL (live rep rows) or a raw S3 key (demo
      // snapshot) — GET_IMAGE resolves both to a usable src, so Justin's
      // photo shows instead of a broken-image icon.
      src={GET_IMAGE(image)}
      alt={name}
      sx={{ width: 28, height: 28, borderRadius: "8px", objectFit: "cover", flex: "0 0 28px" }}
    />
  ) : (
    <SoftBox
      sx={{
        width: 28,
        height: 28,
        borderRadius: "8px",
        flex: "0 0 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT,
        fontSize: "12px",
        fontWeight: 600,
        color: "#FFFFFF",
        background: color,
      }}
    >
      {initials}
    </SoftBox>
  );
}

/** Deterministic accent color for an avatar from its id. */
export function hueFor(id = "") {
  const palette = ["#1B598E", "#7A5AF8", "#06AED4", "#F79009", "#F04438", "#12B76A", "#DD2590", "#6172F3", "#EE46BC"];
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h + id.charCodeAt(i)) % palette.length;
  return palette[h];
}

/** Reset a paged view to page 0 whenever the inputs change. */
export function useResetPage(page, setPage, dep) {
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);
  return page;
}
